import React from 'react';
import { TrendingUp, Brain, Users, Zap, Target, Clock } from 'lucide-react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardContent } from '../ui/TaraCard';
import TaraBadge from '../ui/TaraBadge';

const CareerOperatingPanel = () => {
  // Career energy levels based on planetary positions
  const energyLevels = {
    leadership: 78,
    innovation: 85,
    productivity: 72,
    communication: 68,
    riskTaking: 55,
    burnout: 35, // Lower is better
  };

  // Role suitability matrix
  const roleSuitability = [
    { role: 'Strategy & Planning', score: 82, color: '#6366f1' },
    { role: 'Creative & Design', score: 75, color: '#ec4899' },
    { role: 'People Management', score: 68, color: '#10b981' },
    { role: 'Technical/Analytical', score: 88, color: '#8b5cf6' },
    { role: 'Founder/CEO', score: 65, color: '#f59e0b' },
  ];

  // Workday rhythm - best performance hours
  const workdayRhythm = [
    { time: '6-9 AM', activity: 'Deep Work', energy: 85, planet: 'Mercury' },
    { time: '9-12 PM', activity: 'Meetings', energy: 75, planet: 'Sun' },
    { time: '12-3 PM', activity: 'Creative Tasks', energy: 65, planet: 'Venus' },
    { time: '3-6 PM', activity: 'Admin Work', energy: 70, planet: 'Mars' },
    { time: '6-9 PM', activity: 'Learning', energy: 60, planet: 'Jupiter' },
  ];

  // Career peak timeline
  const careerPeaks = [
    { year: 2025, age: 28, event: 'Early Peak', intensity: 'medium' },
    { year: 2032, age: 35, event: 'Major Peak', intensity: 'high' },
    { year: 2045, age: 48, event: 'Late Peak', intensity: 'high' },
  ];

  return (
    <div className="space-y-6">
      {/* Energy Levels Gauge */}
      <TaraCard variant="cosmic">
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-sacred-500" />
            Career Energy Levels
          </TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Based on 10th house and Saturn analysis
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-4">
            {Object.entries(energyLevels).map(([key, value]) => {
              const isInverted = key === 'burnout';
              const displayValue = isInverted ? 100 - value : value;
              const color = displayValue >= 70 ? '#10b981' : displayValue >= 50 ? '#f59e0b' : '#ef4444';
              
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-stone-700 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className="text-sm font-medium" style={{ color }}>
                      {displayValue}%
                    </span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${displayValue}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Role Suitability Matrix */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-cosmic-500" />
            Role Suitability Matrix
          </TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            What roles best suit your chart
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {roleSuitability.map((item) => (
              <div key={item.role} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-stone-900">
                      {item.role}
                    </span>
                    <span className="text-sm font-medium" style={{ color: item.color }}>
                      {item.score}%
                    </span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${item.score}%`, backgroundColor: item.color }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Workday Rhythm */}
      <TaraCard variant="sacred">
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-sacred-600" />
            Your Workday Rhythm
          </TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Personalized by planetary hours
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {workdayRhythm.map((slot) => (
              <div
                key={slot.time}
                className="flex items-center justify-between p-3 bg-white/50 rounded-lg backdrop-blur-sm"
              >
                <div>
                  <div className="font-medium text-stone-900 text-sm">{slot.time}</div>
                  <div className="text-xs text-stone-600">{slot.activity}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-stone-900">{slot.energy}%</div>
                  <div className="text-xs text-stone-500">{slot.planet}</div>
                </div>
              </div>
            ))}
          </div>
        </TaraCardContent>
      </TaraCard>

      {/* Career Peak Graph */}
      <TaraCard>
        <TaraCardHeader>
          <TaraCardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Career Peak Timeline
          </TaraCardTitle>
          <p className="text-sm text-stone-600 mt-1">
            Based on Saturn + Rahu + 10th house
          </p>
        </TaraCardHeader>
        <TaraCardContent>
          <div className="space-y-3">
            {careerPeaks.map((peak) => (
              <div
                key={peak.year}
                className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-white rounded-lg border border-green-100"
              >
                <div>
                  <div className="font-serif text-lg font-medium text-stone-900">
                    Age {peak.age} ({peak.year})
                  </div>
                  <div className="text-sm text-stone-600">{peak.event}</div>
                </div>
                <TaraBadge
                  variant={peak.intensity === 'high' ? 'transforming' : 'watchful'}
                >
                  {peak.intensity}
                </TaraBadge>
              </div>
            ))}
          </div>
        </TaraCardContent>
      </TaraCard>
    </div>
  );
};

export default CareerOperatingPanel;